export const AGENT_LOG_EVENT = {
  ERROR: 'agent.error',
  OUTPUT_MESSAGE: 'agent.output_message',
  INIT: 'agent.init'
} as const;

export function bodyIncludesAgentLogEvent(
  body: string,
  event: (typeof AGENT_LOG_EVENT)[keyof typeof AGENT_LOG_EVENT]
): boolean {
  return body.toLowerCase().includes(event);
}

export function stripAgentOutputMessagePrefix(body: string): string {
  const lower = body.toLowerCase();
  const prefix = AGENT_LOG_EVENT.OUTPUT_MESSAGE;
  const idx = lower.indexOf(prefix);
  if (idx === -1) {
    return body;
  }
  return body
    .slice(idx + prefix.length)
    .replace(/^\s*-\s*/, '')
    .trim();
}

/**
 * Extracts a balanced JSON object from a string starting at a given position.
 * Handles nested braces and braces inside string literals.
 */
export function extractJsonObjectAt(
  text: string,
  startPos: number
): string | null {
  if (startPos >= text.length || text[startPos] !== '{') {
    return null;
  }

  let braceCount = 0;
  let i = startPos;
  let inString = false;
  let escapeNext = false;

  while (i < text.length) {
    const char = text[i];

    if (escapeNext) {
      escapeNext = false;
      i += 1;
    } else if (char === '\\') {
      escapeNext = true;
      i += 1;
    } else {
      if (char === '"' && !escapeNext) {
        inString = !inString;
      }

      if (!inString) {
        if (char === '{') {
          braceCount += 1;
        } else if (char === '}') {
          braceCount -= 1;
          if (braceCount === 0) {
            return text.substring(startPos, i + 1);
          }
        }
      }

      i += 1;
    }
  }

  return null;
}

const LLM_RESPONSE_NARRATION = /^Response generated by llm:\s*/i;
const OUTPUT_FIELD_MARKER = 'output=';

/**
 * Extracts the payload of an agent.output_message log: only the object inside
 * `output=` when the body follows the `{sources=[...], output={...}}` wrapper,
 * otherwise the response text without the event prefix and llm narration.
 */
export function extractAgentOutputMessagePayload(body: string): string {
  const stripped = stripAgentOutputMessagePrefix(body).replace(
    LLM_RESPONSE_NARRATION,
    ''
  );

  const outputIndex = stripped.indexOf(OUTPUT_FIELD_MARKER);
  if (outputIndex === -1) {
    return stripped;
  }

  const outputStart = outputIndex + OUTPUT_FIELD_MARKER.length;
  const jsonObject = extractJsonObjectAt(stripped, outputStart);
  if (jsonObject) {
    return jsonObject;
  }

  const rawOutput = stripped.slice(outputStart).trim();
  return rawOutput || stripped;
}
